import { RefObject } from 'react';
import styled from 'styled-components';
import { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';

import { Box } from '../../../../common/ui/Box';
import { theme } from '../../../../common/ui/theme';
import { CategoryColumn } from '../hooks/useScheduleProductColumns';
import { ScheduleLayout } from '../scheduleLayout';

import { ScheduleProductColumn } from './ScheduleProductColumn';

const SwiperContainer = styled(Box)`
    position: relative;
    overflow: hidden;
    flex: 1;
    margin-left: -4px;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: ${ScheduleLayout.PRODUCT_COLUMN_SPACER_END};
        height: 100%;
        background: linear-gradient(to right, transparent, ${theme.colors.background.default});
        pointer-events: none;
        z-index: 10;
    }
`;

const getColumnWidth = (columnIndex: number, totalColumns: number): string => {
    if (columnIndex === 0) {
        return ScheduleLayout.PRODUCT_COLUMN_WIDTH_FIRST;
    }
    if (columnIndex === totalColumns - 1) {
        return ScheduleLayout.PRODUCT_COLUMN_WIDTH_LAST;
    }
    return ScheduleLayout.PRODUCT_COLUMN_WIDTH;
};

interface ScheduleProductCategorySliderProps {
    columns: CategoryColumn[];
    onSwiperInit?: (swiper: SwiperType) => void;
    prevButtonRef: RefObject<HTMLButtonElement | null>;
    nextButtonRef: RefObject<HTMLButtonElement | null>;
}

export const ScheduleProductCategorySlider = ({
    columns,
    onSwiperInit,
    prevButtonRef,
    nextButtonRef,
}: ScheduleProductCategorySliderProps) => {
    return (
        <SwiperContainer>
            <Swiper
                modules={[Navigation]}
                slidesPerView="auto"
                slidesPerGroup={1}
                slidesPerGroupAuto={false}
                spaceBetween={0}
                slidesOffsetBefore={4}
                slidesOffsetAfter={-4}
                grabCursor={true}
                navigation={{
                    prevEl: prevButtonRef?.current,
                    nextEl: nextButtonRef?.current,
                }}
                onBeforeInit={(swiper) => {
                    if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
                        swiper.params.navigation.prevEl = prevButtonRef?.current;
                        swiper.params.navigation.nextEl = nextButtonRef?.current;
                    }
                }}
                onSwiper={(swiper) => {
                    if (onSwiperInit) {
                        onSwiperInit(swiper);
                    }
                    // Initialize navigation after swiper is ready
                    if (swiper.navigation) {
                        swiper.navigation.init();
                        swiper.navigation.update();
                    }
                }}
            >
                {columns.map((column, columnIndex) => {
                    const isFirstColumn = columnIndex === 0;
                    const isLastColumn = columnIndex === columns.length - 1;
                    const width = getColumnWidth(columnIndex, columns.length);

                    return (
                        <SwiperSlide key={column.key} style={{ width }}>
                            <ScheduleProductColumn
                                column={column}
                                isFirstColumn={isFirstColumn}
                                isLastColumn={isLastColumn}
                            />
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </SwiperContainer>
    );
};
